import MapDrawManager from '@/lib/mapbox/map-draw-manager'
import WaypointEditMode from '@/lib/mapbox/waypoint-edit-mode'
import StaticEditMode from '@/lib/mapbox/static-edit-mode'
import TrimMode from '@/lib/mapbox/trim-mode'
import LineEditMode from '@/lib/mapbox/line-edit-mode'
import FreehandDrawLineMode from '@/lib/mapbox/freehand-draw-line-mode'
import ShapeEditMode from '@/lib/mapbox/shape-edit-mode'
import FreehandDrawShapeMode from '@/lib/mapbox/freehand-draw-shape-mode'
import ConnectMode from '@/lib/mapbox/connect-mode'
import SplitMode from '@/lib/mapbox/split-mode'
import RouteCreateMode from '@/lib/mapbox/route-create-mode'
import RouteViewMode from '@/lib/mapbox/route-view-mode'
import { findLineDistance, findArea } from '@/lib/math'
import markup from '@/lib/markup'
import markupService from '@/services/markup-service'
import { getItem } from '@/plugins/storage'
import { watch } from 'vue'
import { mapReadyPromise } from '@/services/map-service'
import LineViewMode from '@/lib/mapbox/line-view-mode'
import ShapeViewMode from '@/lib/mapbox/shape-view-mode'
import { MARKUP_COORDINATES_CHANGED } from '@/lib/mapbox/drawing-events'

let drawManager = null

/**
 * *(AI-generated)*
 * Sets the draw manager to waypoint edit mode.
 */
export const setupDrawWaypointMode = () => {
  drawManager.setMode(WaypointEditMode)
}

/**
 * *(AI-generated)*
 * Sets the draw manager to the appropriate track draw mode.
 */
export const setupDrawTrackMode = (drawMode) => {
  switch (drawMode) {
    case 'editing':
      drawManager.setMode(StaticEditMode)
      break
    case 'trimming':
      drawManager.setMode(TrimMode)
      break
  }
}

/**
 * *(AI-generated)*
 * Sets the draw manager to the appropriate line draw mode.
 */
export const setupDrawLineMode = (drawMode) => {
  switch (drawMode) {
    case 'editing':
      drawManager.setMode(LineEditMode, {
        canUndo: true,
      })
      break
    case 'click-drawing':
      drawManager.setMode(LineEditMode, {
        isCreate: true,
        canUndo: true,
      })
      break
    case 'freehand-drawing':
      drawManager.setMode(FreehandDrawLineMode)
      break
    case 'connect':
      drawManager.setMode(ConnectMode)
      break
    case 'split':
      drawManager.setMode(SplitMode)
      break
    case 'line-view':
      drawManager.setMode(LineViewMode)
  }
}

/**
 * *(AI-generated)*
 * Sets the draw manager to the appropriate route draw mode.
 */
export const setupDrawRouteMode = (drawMode) => {
  switch (drawMode) {
    case 'route-create':
      drawManager.setMode(RouteCreateMode)
      break
    case 'route-view':
      drawManager.setMode(RouteViewMode)
      break
  }
}

/**
 * *(AI-generated)*
 * Sets the draw manager to the appropriate shape draw mode.
 */
export const setupDrawShapeMode = (drawMode) => {
  switch (drawMode) {
    case 'editing':
      drawManager.setMode(ShapeEditMode, {
        canUndo: true,
      })
      break
    case 'click-drawing':
      drawManager.setMode(ShapeEditMode, {
        isCreate: true,
        canUndo: true,
      })
      break
    case 'freehand-drawing':
      drawManager.setMode(FreehandDrawShapeMode)
      break
    case 'shape-view':
      drawManager.setMode(ShapeViewMode, {
        isCreate: false,
        canUndo: false,
      })
      break
  }
}

/**
 * *(AI-generated)*
 * Selects and activates the correct draw mode for a given markup type and draw mode string.
 */
export const setupMarkupDrawMode = (markupType, drawMode) => {
  switch (markupType) {
    case 'waypoint':
      setupDrawWaypointMode()
      break
    case 'line':
      setupDrawLineMode(drawMode)
      break
    case 'track':
      setupDrawTrackMode(drawMode)
      break
    case 'shape':
      setupDrawShapeMode(drawMode)
      break
    case 'route':
      setupDrawRouteMode(drawMode)
      break
  }
}

/**
 * *(AI-generated)*
 * Initializes the draw manager with geojson data and activates the appropriate markup mode.
 */
export const setupMarkupMode = (geoJson, isDrawing) => {
  drawManager.setDrawing(isDrawing)

  drawManager.setData(geoJson)

  const markupType = geoJson.properties.type
  const drawMode = markupService.getFocusedMarkup().mode
  setupMarkupDrawMode(markupType, drawMode)
}

/**
 * *(AI-generated)*
 * Ends the current draw session and optionally resets the drawing state.
 */
export const teardownMarkupMode = (isEditMode = true) => {
  drawManager.endDraw()
  if (isEditMode) {
    // don't reset the isDrawing() state when tearing down the view cards, it may
    // overwrite a valid state from the setup call
    drawManager.setDrawing(false)
  }
}

/**
 * *(AI-generated)*
 * Updates the draw manager with new geojson for the markup being edited.
 */
export const setEditingMarkupGeoJson = (geoJson) => {
  drawManager.setData(geoJson)
}

/**
 * *(AI-generated)*
 * Loads geojson into the draw manager and activates view mode for the markup.
 */
export const startMarkupViewMode = (geoJson) => {
  drawManager.setData(geoJson)

  const markupType = geoJson.properties.type
  const drawMode = markupService.getFocusedMarkup().mode
  setupMarkupDrawMode(markupType, drawMode)
}

/**
 * *(AI-generated)*
 * Handles coordinate changes from the draw manager. Updates the focused markup's view model,
 * falling back to looking up the markup by ID if no markup is currently focused.
 */
export const handleMarkupCoordinatesChanged = ({ id, coordinates }) => {
  const focusedMarkup = markupService.getFocusedMarkup()

  // FUTURE: Remove once markup edit screen reacts to coordinate changes.
  // Fallback to `|| markupService.getMarkupByID(id)`. Conditionally called when focusedMarkup?.markup is falsy
  // in theory this is a when markup is being edited but not yet focused.
  const markupViewModel =
    focusedMarkup?.markup || markupService.getMarkupByID(id)

  /**
   * If coordinates is invalid, manually reset coordinates to match initial create state
   * package markupService validates and throws on empty coordinates array
   * isEmpty if
   * - coordinates is falsy
   * - coordinates is an empty array
   * - coordinates is an array with one element that is an empty array
   *
   * Initial state for shape is [[]], initial state for line is []
   */
  const isEmpty =
    !coordinates ||
    coordinates.length === 0 ||
    (coordinates.length === 1 && coordinates[0]?.length === 0)
  if (isEmpty && markupViewModel.isCreate) {
    // MarkupService Line and Shape providers / class implementations set an inital value
    // for coordinates is an empty array [] or array with empty array [[]]
    const emptyCoords = markupViewModel.type === 'shape' ? [[]] : []
    markupViewModel.coordinates = emptyCoords
    // The MarkupService Line provider sets origCoordinates
    if ('origCoordinates' in markupViewModel) {
      markupViewModel.origCoordinates = emptyCoords
    }
    return
  }

  // Update coordinates normally - package validation will handle invalid cases
  markupViewModel.setFeatureCoordinates(coordinates)

  const lengthElement = document.getElementById('markup-edit-length')
  if (lengthElement) {
    const coords =
      focusedMarkup.markup.type === 'shape' ? coordinates[0] : coordinates
    const length = findLineDistance(coords)
    const formattedLength = markup.distanceFormatter(length)
    lengthElement.innerHTML = `${formattedLength.distance} <span class="uom">${formattedLength.UOM}</span>`
  }
  const areaElement = document.getElementById('markup-edit-area')
  if (areaElement) {
    const unitType = getItem('unitType')
    const area = findArea(coordinates[0], unitType)
    areaElement.innerHTML = `${area.area} <span class="uom">${area.UOM}</span>`
  }
}

/**
 * *(AI-generated)*
 * Initializes the markup draw manager and registers watchers for markup mode changes.
 */
export const setupMarkupEditManager = (mapViewer) => {
  // initialize manager
  drawManager = new MapDrawManager({ mapViewer })

  // drawing events
  drawManager.events.on(MARKUP_COORDINATES_CHANGED, (event) =>
    handleMarkupCoordinatesChanged(event.detail)
  )

  // add watchers for modes
  watch(
    () => markupService.getEditingMarkupGeoJson(),
    (geoJson, prevGeoJson) => setMarkupMode(geoJson, prevGeoJson, true),
    { deep: true }
  )
  watch(
    () => markupService.getViewModeMarkupGeoJson(),
    (geoJson, prevGeoJson) => setMarkupMode(geoJson, prevGeoJson, false),
    { deep: true }
  )
  watch(() => markupService.getFocusedMarkup().mode, modeSetup)
}

/**
 * *(AI-generated)*
 * Waits for the map to be ready and then sets the draw mode for the current editing markup.
 */
export const modeSetup = async (mode) => {
  await mapReadyPromise
  const geoJson = markupService.getEditingMarkupGeoJson()

  if (!geoJson) {
    return
  }

  const markupType = geoJson.properties.type

  setupMarkupDrawMode(markupType, mode)
}

/**
 * *(AI-generated)*
 * Sets up or tears down markup draw mode based on changes to the active markup geojson.
 */
export const setMarkupMode = async (geoJson, prevGeoJson, isEditMode) => {
  await mapReadyPromise
  const uuid = geoJson?.id ?? null
  const prevUuid = prevGeoJson?.id ?? null
  const starting = uuid !== null && prevUuid === null
  const ending = uuid === null && prevUuid !== null

  if (starting) {
    setupMarkupMode(geoJson, isEditMode)
  } else if (ending) {
    teardownMarkupMode(isEditMode)
  }

  if (geoJson) {
    drawManager.setData(geoJson)
  }
}

/**
 * Returns whether drawing mode is currently active.
 *
 * Safe to call before initialization - returns false if drawManager
 * hasn't been initialized yet (before first map sourcedata event).
 *
 * @return {boolean} True if user is actively drawing/editing markups, false otherwise
 */
export const isDrawing = () => drawManager?.isDrawing() ?? false

/**
 * *(AI-generated)*
 * Initializes the markup draw manager plugin for the given map viewer.
 */
export default function (mapManager) {
  // setup manager when map is ready
  mapManager.events.once('map-x-source-data', () =>
    setupMarkupEditManager(mapManager.viewer)
  )
}
